import { equals } from 'ramda'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { isElectron } from '~/utils/common'

import { SharedVariables } from '@mangovoice/types'

// Global subscription manager to prevent multiple IPC listeners
class SharedVariableManager {
  private subscriptions = new Map<string, Set<(value: any) => void>>()
  private cleanupFunctions = new Map<string, () => void>()
  private values = new Map<string, any>()

  subscribe(variableName: string, callback: (value: any) => void) {
    if (!this.subscriptions.has(variableName)) {
      this.subscriptions.set(variableName, new Set())

      // Create single IPC subscription for this variable
      const cleanup = window.electron?.sharedVariables?.subscribe(
        variableName,
        (value: any) => {
          this.values.set(variableName, value)
          const callbacks = this.subscriptions.get(variableName)
          // Use queueMicrotask to defer callbacks and prevent synchronous updates during render
          queueMicrotask(() => {
            callbacks?.forEach((cb) => cb(value))
          })
        },
      )

      if (cleanup) {
        this.cleanupFunctions.set(variableName, cleanup)
      }
    }

    this.subscriptions.get(variableName)!.add(callback)

    return () => {
      const callbacks = this.subscriptions.get(variableName)
      if (callbacks) {
        callbacks.delete(callback)

        // If no more callbacks, cleanup IPC subscription
        if (callbacks.size === 0) {
          const cleanup = this.cleanupFunctions.get(variableName)
          cleanup?.()
          this.subscriptions.delete(variableName)
          this.cleanupFunctions.delete(variableName)
          this.values.delete(variableName)
        }
      }
    }
  }

  getValue(variableName: string) {
    return this.values.get(variableName)
  }
}

const globalManager = new SharedVariableManager()

export function useSharedVariable<T>(
  variableName: keyof typeof SharedVariables,
  defaultValue?: T | null,
) {
  const [value, setValue] = useState<T | null>(() => {
    return defaultValue !== undefined ? defaultValue : null
  })

  // Store defaultValue in a ref to prevent infinite loops when defaultValue is an object/array
  const defaultValueRef = useRef(defaultValue)
  defaultValueRef.current = defaultValue

  const noopSetter = useCallback(() => {}, [])

  useEffect(() => {
    // Early return for non-Electron environments to prevent infinite loops
    if (!isElectron) {
      return
    }

    let isMounted = true

    window.electron?.sharedVariables
      ?.getValue(variableName)
      .then((initialValue: T) => {
        if (isMounted) {
          setValue(initialValue)
        }
      })
      .catch((error: any) => {
        console.error('Error fetching initial value for', variableName, error)

        if (isMounted && defaultValueRef.current !== undefined) {
          setValue(defaultValueRef.current)
        }
      })

    return () => {
      isMounted = false
    }
  }, [variableName])

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!isElectron) {
        return () => {}
      }

      const handleUpdate = (newValueFromElectron: T) => {
        setValue((currentLocalValue) => {
          if (!equals(currentLocalValue, newValueFromElectron)) {
            return newValueFromElectron
          }
          return currentLocalValue
        })

        onStoreChange()
      }

      return globalManager.subscribe(variableName, handleUpdate)
    },
    [variableName],
  )

  const getSnapshot = useCallback(() => {
    return value
  }, [value])

  const syncedValue = useSyncExternalStore(subscribe, getSnapshot)

  const updateValue = useCallback(
    (newValue: T | ((prev: T | null) => T)) => {
      if (!isElectron) {
        return
      }

      // Handle functional updates by executing them locally first
      const resolvedValue =
        typeof newValue === 'function'
          ? (newValue as (prev: T | null) => T)(value)
          : newValue

      window.electron?.sharedVariables?.setValue(variableName, resolvedValue)
    },
    [variableName, value],
  )

  // Early return for non-Electron environments to prevent infinite loops
  if (!isElectron) {
    return [defaultValue as T, noopSetter] as const
  }

  return [syncedValue, updateValue] as const
}
